import React, { forwardRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { useDsTokens } from '../theme/useDsTokens';
import { DsResolvedTokens } from '../theme/tokens';
import { cn } from '../utils/cn';
import { ChipVariant, DsPrimitiveProps } from './types';

export interface ChipProps extends DsPrimitiveProps {
  label: string;
  variant?: ChipVariant;
  selected?: boolean;
  icon?: React.ReactElement;
  /** Dynamic queue color from backend */
  queueColor?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onDelete?: () => void;
}

interface StyleProps {
  tokens: DsResolvedTokens;
  bg: string;
  fg: string;
  disabled: boolean;
  clickable: boolean;
}

function resolveChipColors(
  variant: ChipVariant,
  selected: boolean,
  queueColor: string | undefined,
  tokens: DsResolvedTokens
): { bg: string; fg: string } {
  const effectiveVariant = selected ? 'filter-active' : variant;

  switch (effectiveVariant) {
    case 'filter-active':
      return { bg: tokens.brandPrimarySubtle, fg: tokens.brandPrimary };
    case 'status-success':
      return {
        bg: applyAlpha(tokens.statusSuccess, tokens.opacitySubtle),
        fg: tokens.statusSuccess,
      };
    case 'status-warning':
      return {
        bg: applyAlpha(tokens.statusWarning, tokens.opacitySubtle),
        fg: tokens.statusWarning,
      };
    case 'status-error':
      return {
        bg: applyAlpha(tokens.statusError, tokens.opacitySubtle),
        fg: tokens.statusError,
      };
    case 'queue':
      if (queueColor) {
        return {
          bg: applyAlpha(queueColor, tokens.opacitySubtle),
          fg: queueColor,
        };
      }
      return { bg: tokens.surfaceSunken, fg: tokens.textPrimary };
    case 'neutral':
    default:
      return { bg: tokens.surfaceSunken, fg: tokens.textPrimary };
  }
}

function applyAlpha(hexColor: string, alpha: number): string {
  if (hexColor.startsWith('rgba') || hexColor.startsWith('rgb')) {
    return hexColor;
  }
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const useStyles = makeStyles({
  root: (props: StyleProps) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: props.tokens.spaceHalf,
    height: props.tokens.space3,
    maxWidth: props.tokens.space6 * 4,
    padding: `0 ${props.tokens.space1}px`,
    borderRadius: props.tokens.radiusSm,
    backgroundColor: props.bg,
    color: props.fg,
    fontSize: props.tokens.fontSizeCaption,
    fontWeight: props.tokens.fontWeightMedium,
    lineHeight: 1,
    opacity: props.disabled ? props.tokens.opacityDisabled : 1,
    border: 'none',
    cursor: props.clickable && !props.disabled ? 'pointer' : 'default',
    transition: `background-color ${props.tokens.motionDurationMicro}ms ${props.tokens.motionEasingStandard}`,
    '&:hover': props.clickable && !props.disabled
      ? { backgroundColor: props.tokens.interactionHover }
      : {},
    '&.Mui-focusVisible, &:focus-visible': {
      outline: `${props.tokens.borderWidthStrong}px solid ${props.tokens.borderFocus}`,
      outlineOffset: 2,
    },
  }),
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  icon: {
    display: 'inline-flex',
    fontSize: 16,
    flexShrink: 0,
  },
});

export const Chip = forwardRef<HTMLButtonElement | HTMLSpanElement, ChipProps>(function Chip(
  {
    id,
    className,
    disabled = false,
    variant = 'neutral',
    label,
    selected = false,
    icon,
    queueColor,
    onClick,
    onDelete,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();
  const clickable = Boolean(onClick);
  const colors = resolveChipColors(variant, selected, queueColor, tokens);

  const classes = useStyles({
    tokens,
    ...colors,
    disabled,
    clickable,
  });

  const content = (
    <>
      {icon && <span className={classes.icon}>{icon}</span>}
      <span className={classes.label}>{label}</span>
      {onDelete && !disabled && (
        <button
          type="button"
          aria-label={`Remover ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'inherit',
            opacity: 0.7,
          }}
        >
          <CloseIcon style={{ fontSize: 14 }} />
        </button>
      )}
    </>
  );

  if (clickable) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        id={id}
        type="button"
        disabled={disabled}
        className={cn(classes.root, className)}
        data-testid={testId}
        aria-pressed={selected || undefined}
        onClick={onClick}
      >
        {content}
      </button>
    );
  }

  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      id={id}
      className={cn(classes.root, className)}
      data-testid={testId}
      aria-disabled={disabled || undefined}
    >
      {content}
    </span>
  );
});

Chip.displayName = 'Chip';
